import { cva, type VariantProps } from 'class-variance-authority'

import { cn } from '@/customer/lib/utils'

const underline = cva('mt-1 h-2 w-full md:h-3', {
  variants: {
    color: {
      yellow: 'bg-yellow',
      blue: 'bg-blue',
      pink: 'bg-pink',
      'baby-blue': 'bg-baby-blue',
      green: 'bg-green',
      orange: 'bg-orange',
      destructive: 'bg-destructive',
    },
  },
})

export type StackedHeadlineColor = NonNullable<VariantProps<typeof underline>['color']>

export type StackedHeadlineItem = { text: string; color: StackedHeadlineColor }

/**
 * The stacked-headline pattern from DESIGN.md.
 * Each line gets a different brand-coloured underline; consumers pass the cycle
 * explicitly so adjacent screens can vary the rhythm.
 */
export function StackedHeadline({
  lines,
  align = 'left',
  wrap = false,
  margin = false,
  className,
}: {
  lines: StackedHeadlineItem[]
  align?: 'left' | 'center'
  /**
   * `false` (default) stacks each entry on its own row - the
   * canonical DESIGN.md pattern. `true` flows the entries as a
   * `flex-wrap` row so short headlines like `['Your', 'Pets']`
   * can share a line before falling back to wrapping.
   */
  wrap?: boolean
  /**
   * When `true`, applies the standard `mb-8 md:mb-12` below the
   * headline. Portal pages consistently sit the next block at that
   * distance from the headline, so having it baked in stops call
   * sites drifting into ad-hoc `className="mb-*"` values.
   */
  margin?: boolean
  className?: string
}) {
  return (
    <div
      className={cn(
        'flex gap-3',
        wrap ? 'flex-row flex-wrap' : 'flex-col',
        align === 'center'
          ? wrap
            ? 'justify-center'
            : 'items-center'
          : wrap
            ? 'justify-start'
            : 'items-start',
        margin && 'mb-8 md:mb-12',
        className
      )}
    >
      {lines.map((line) => (
        <div key={line.text} className="inline-flex flex-col">
          <span className="text-headline-lg-mobile md:text-headline-lg leading-none">
            {line.text}
          </span>
          <div className={underline({ color: line.color })} />
        </div>
      ))}
    </div>
  )
}
